<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1>{{ $t('subscriptions.views.SubscriptionClosedProjects.closedRooms') }}</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="d-flex align-center">
          <SearchTextField v-model="searchQuery"/>
          <app-button 
            type="outlined"
            variant="neutral"
            class="ml-4"
            :height="40"
            @click="openFiltersPane"
          >
            <template v-if="filtersCount === 0">
              <app-material-icon name="sliders" type="fa" size="xs" />
            </template>
            <template v-else-if="filtersCount > 0">
              <v-avatar color="primary"
                        class="text-white text-caption mr-2"
                        size="19"
              >
                {{ filtersCount }}
              </v-avatar>
            </template>
            {{ $t('subscriptions.views.SubscriptionClosedProjects.filters') }}
          </app-button>
        </div>
      </v-col>
      <v-col class="text-right">
        <AppDownloadFile :link="exportLink">
          <template #default="{submitForm}">
            <app-button
              type="outlined"
              variant="neutral"
              @click="submitForm"
            >
              <app-material-icon name="file-export" type="fa" size="xs" weight="fal" />
              {{ $t('subscriptions.views.SubscriptionClosedProjects.export') }}
            </app-button>
          </template>
        </AppDownloadFile>
      </v-col>
    </v-row>
    <v-row>
      <DataTableFiltersSummary
        v-if="filtersCount > 0"
        :active-filters="closedRoomsFilters"
        class="mt-4"
        @clear-filter="clearFilter"
        @clear-all-filters="clearAllFilters"
      />
    </v-row>
    <template v-if="closedRoomsPending && !closedRooms">
      <v-row>
        <v-col>
          <AppDataTableSkeletonLoader class="w-100"/>
        </v-col>
      </v-row>
    </template>
    <v-row v-if="closedRooms && !closedRoomsPending">
      <v-col>
        <v-data-table class="SubscriptionClosedProjects-table"
                      :items="filteredClosedRooms"
                      :headers="headers"
                      :page="currentPage"
                      :items-per-page="itemsPerPage"
        >
          <template #item.type="{item}">
            <RoomTypeIcon class="td-mobile-view" :room="item" />
          </template>
          <template #item.cmRef="{item}">
            {{ item.cmRef || '-' }}
          </template>
          <template #item.creator="{item}">
            <div>
              <AppTooltip>
                <template #activator="{ props }">
                  <div class="text-truncated" v-bind="props">
                    {{ item.creator }}
                  </div>
                </template>
                <span>{{ item.creator }}</span>
              </AppTooltip>

              <AppTooltip>
                <template #activator="{ props }">
                  <div class="text-truncated" v-bind="props">
                    <a :href="`mailto:${item.creatorEmail}`"
                       class="creator-email app-link"
                    >
                      {{ item.creatorEmail }}
                    </a>
                  </div>
                </template>
                <span>{{ item.creatorEmail }}</span>
              </AppTooltip>
            </div>
          </template>
          <template #item.createdAt="{item}">
            <DateDisplay :value="item.createdAt" />
          </template>
          <template #item.deletedAt="{item}">
            <SubscriptionClosedProjectsCellDeletedAt v-if="item.deletedAt"
                                                     :deletedAt="item.deletedAt"
                                                     :roomId="item.id"
                                                     :todayCache="dayjsToday"
            />
            <span v-else>-</span>
          </template>
          <template #item.users="{item}">
            <template v-if="item.usersCount !== -1">{{ item.usersCount }}</template>
            <template v-else>-</template>
          </template>
          <template #item.storage="{item}">
            <app-filesize-display v-if="item.storage && item.storage !== -1" :value="item.storage" />
            <div v-else>-</div>
          </template>
          <template #item.signedDocuments="{item}">
            <template v-if="item.signedDocuments !== -1">{{ item.signedDocuments }}</template>
            <template v-else>-</template>
          </template>

          <template #bottom>
            <div class="d-flex justify-end mt-4">
              <AppPagination v-model:current-page="currentPage"
                             v-model:page-size="itemsPerPage"
                             :pageSizes="ITEMS_PER_PAGE"
                             :total="filteredClosedRooms.length"
              />
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>

  <SubscriptionClosedProjectsFiltersPane />

  <RestoreRoomDialog
      v-if="restoreRoomDialog"
      :roomId="restoreRoomDialog"
      @confirm="confirmRestoreRoomDialog"
      @close="closeRestoreRoomDialog"
  />
</template>

<script>
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapMutations, mapState, useStore } from 'vuex'

import AppDataTableSkeletonLoader from '@/common/AppDataTableSkeletonLoader.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import DataTableFiltersSummary from '@/common/filters/DataTableFiltersSummary.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import RoomTypeIcon from '@/common/RoomTypeIcon.vue'
import SearchTextField from '@/common/SearchTextField.vue'
import { toast } from '@/common/utils/toast'
import { GET_CLOSED_ROOMS } from '@/store/modules/subscription/action_types'
import {
  REMOVE_CLOSED_ROOMS_FILTER,
  SET_CLOSED_ROOMS_FILTERS,
  SET_CLOSED_ROOMS_FILTERS_PANE_IS_OPEN,
  SET_RESTORE_ROOM_DIALOG,
} from '@/store/modules/subscription/mutation_types'

import { SUBSCRIPTION_CLOSED_PROJECTS_ITEMS_PER_PAGE } from '../constants.js'
import RestoreRoomDialog from '../RestoreRoomDialog.vue'
import SubscriptionClosedProjectsCellDeletedAt from '../SubscriptionClosedProjectsCellDeletedAt.vue'
import SubscriptionClosedProjectsFiltersPane from '../SubscriptionClosedProjectsFiltersPane.vue'

const ITEMS_PER_PAGE = [50, 100, 250]

export default {
  name: 'SubscriptionClosedProjects',
  components: {
    AppTooltip,
    AppDataTableSkeletonLoader,
    AppDownloadFile,
    AppPagination,
    DataTableFiltersSummary,
    DateDisplay,
    RestoreRoomDialog,
    RoomTypeIcon,
    SearchTextField,
    SubscriptionClosedProjectsCellDeletedAt,
    SubscriptionClosedProjectsFiltersPane,
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: SUBSCRIPTION_CLOSED_PROJECTS_ITEMS_PER_PAGE,
      pageSize: ITEMS_PER_PAGE[0],
    })

    return {
      currentPage,
      itemsPerPage,
    }
  },
  data () {
    return {
      headers: [
        {
          value: 'type',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.type'),
          sortable: true,
        },
        {
          value: 'name',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.name'),
          sortable: true,
        },
        {
          value: 'cmRef',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.cmRef'),
          sortable: true,
        },
        {
          value: 'creator',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.creator'),
          sortable: true,
        },
        {
          value: 'createdAt',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.creationDate'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'deletedAt',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.deletedIn'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'users',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.users'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'storage',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.size'),
          sortable: true,
          align: 'center',
        },
        {
          value: 'signedDocuments',
          title: this.$t('subscriptions.views.SubscriptionClosedProjects.headers.signedDocuments'),
          sortable: true,
          align: 'center',
        },
      ],
      searchQuery: '',
      ITEMS_PER_PAGE,
    }
  },
  computed: {
    ...mapState('subscription', ['closedRooms', 'closedRoomsPending', 'restoreRoomDialog', 'closedRoomsFilters']),
    ...mapGetters('user', ['currentUserId']),
    filtersCount () {
      return this.closedRoomsFilters.length
    },
    filteredClosedRooms () {
      let filteredClosedRooms = cloneDeep(this.closedRooms.data)
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filteredClosedRooms = filteredClosedRooms.filter(closedRoom => {
          return closedRoom.name.toLowerCase().includes(query) ||
            closedRoom.creator.toLowerCase().includes(query) ||
            closedRoom.creatorEmail.toLowerCase().includes(query)
        })
      }

      const memberFilters = this.closedRoomsFilters.filter(filter => filter.category.key === 'members')
      if (memberFilters.length > 0) {
        filteredClosedRooms = filteredClosedRooms.filter(room => {
          return memberFilters.some((filterMember) => room.users.some((roomUser) => filterMember.value.key === roomUser.fullName))
        })
      }
      const projectTypeFilters = this.closedRoomsFilters.filter(filter => filter.category.key === 'projectType')
      if (projectTypeFilters.length > 0 && projectTypeFilters.length !== 2) {
        if (projectTypeFilters[0].value.key === 'project') {
          filteredClosedRooms = filteredClosedRooms.filter(room => { return !room.isDataroom })
        }
        if (projectTypeFilters[0].value.key === 'dataroom') {
          filteredClosedRooms = filteredClosedRooms.filter(room => { return room.isDataroom })
        }
      }

      const deletedAtFilter = this.closedRoomsFilters.find(filter => filter.category.key === 'deletedAt')
      if (deletedAtFilter) {
        const filterFrom = deletedAtFilter.value.key[0]
        const filterTo = deletedAtFilter.value.key[1]

        if (filterFrom && !filterTo) {
          filteredClosedRooms = filteredClosedRooms.filter(room => {
            return dayjs(room.deletedAt).isSameOrAfter(dayjs(filterFrom))
          })
        } else if (filterFrom && filterTo) {
          filteredClosedRooms = filteredClosedRooms.filter(room => {
            return dayjs(room.deletedAt).isBetween(dayjs(filterFrom), dayjs(filterTo), 'day', '[]')
          })
        }
      }

      return filteredClosedRooms
    },
    exportLink () {
      return `${import.meta.env.VUE_APP_API_URL}/subscription/download/rooms-excel`
    },
  },
  created () {
    this.prepareGetClosedRooms()
    // We cache a today timestamp for display purposes
    this.dayjsToday = dayjs()
  },
  methods: {
    ...mapMutations('subscription', [SET_CLOSED_ROOMS_FILTERS_PANE_IS_OPEN, SET_CLOSED_ROOMS_FILTERS, REMOVE_CLOSED_ROOMS_FILTER]),
    ...mapActions('subscription', [GET_CLOSED_ROOMS]),
    ...mapMutations('subscription', [SET_RESTORE_ROOM_DIALOG]),
    async prepareGetClosedRooms () {
      try {
        await this.GET_CLOSED_ROOMS()
      } catch (error) {
        console.error(error)
        toast.error(this.$t('subscriptions.views.SubscriptionClosedProjects.getActiveRoomsError'))
      }
    },
    confirmRestoreRoomDialog () {
      this.prepareGetClosedRooms()
      this.closeRestoreRoomDialog()
    },
    closeRestoreRoomDialog () {
      this.SET_RESTORE_ROOM_DIALOG(null)
    },
    openFiltersPane () {
      this.SET_CLOSED_ROOMS_FILTERS_PANE_IS_OPEN(true)
    },
    clearFilter (filter) {
      this.REMOVE_CLOSED_ROOMS_FILTER(filter)
    },
    clearAllFilters () {
      this.SET_CLOSED_ROOMS_FILTERS([])
      this.$emit('update:searchQuery', '')
    },
  },
}
</script>

<style scoped lang="scss">

:deep(.SubscriptionClosedProjects-table) {
  background-color: transparent;

  table {
    border-collapse: separate;
    border-spacing: 0 5px;
  }
  th {
    color: rgb(var(--v-theme-accent)) !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    border-bottom: none !important;
  }
  td {
    color: rgb(var(--v-theme-accent)) !important;
    font-size: 14px !important;
    border-bottom: none !important;
    vertical-align: center;
    height: 52px !important;

    &:first-child {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    &:last-child {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }

  tbody {
    tr {
      background-color: #fff;
    }
  }

  .creator-email {
    max-width: 200px;
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
}
</style>
